class CollectionPanelsCarousel extends HTMLElement {
  static AUTOPLAY_MS = 5000;
  static MOBILE_MAX_WIDTH = '(max-width: 749px)';
  static PANELS_PER_MOBILE_PAGE = 2;

  connectedCallback() {
    this.viewport = this.querySelector('.exposure-collection-panels__viewport');
    this.track = this.querySelector('.exposure-collection-panels__grid');
    this.prevBtn = this.querySelector('[data-carousel-prev]');
    this.nextBtn = this.querySelector('[data-carousel-next]');
    this.section = this.closest('.exposure-collection-panels');
    this.index = 1;
    this.positionCount = 0;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.autoplayTimer = null;
    this.isAnimating = false;
    this.clonesReady = false;
    this.mobilePagesReady = false;
    this.mobileMq = window.matchMedia(CollectionPanelsCarousel.MOBILE_MAX_WIDTH);

    if (!this.viewport || !this.track) return;

    if (this.isMobilePaired()) {
      this.section?.classList.add('is-mobile-paired');
      this.buildMobilePages();
    }

    this.onResize = this.onResize.bind(this);
    this.onPrev = this.onPrev.bind(this);
    this.onNext = this.onNext.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);
    this.onTransitionEnd = this.onTransitionEnd.bind(this);

    this.prevBtn?.addEventListener('click', this.onPrev);
    this.nextBtn?.addEventListener('click', this.onNext);
    this.viewport.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.viewport.addEventListener('touchmove', this.onTouchMove, { passive: false });
    this.viewport.addEventListener('touchend', this.onTouchEnd, { passive: true });
    this.track.addEventListener('transitionend', this.onTransitionEnd);
    window.addEventListener('resize', this.onResize);

    const layoutReady = document.fonts?.ready ?? Promise.resolve();
    const layoutTimeout = new Promise((resolve) => {
      window.setTimeout(resolve, 150);
    });

    Promise.race([layoutReady, layoutTimeout]).then(() => this.init());
  }

  init() {
    if (this.isMobilePaired()) {
      this.enableMobilePairedCarousel();
      return;
    }

    this.enableDesktopCarousel();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    this.prevBtn?.removeEventListener('click', this.onPrev);
    this.nextBtn?.removeEventListener('click', this.onNext);
    this.viewport?.removeEventListener('touchstart', this.onTouchStart);
    this.viewport?.removeEventListener('touchmove', this.onTouchMove);
    this.viewport?.removeEventListener('touchend', this.onTouchEnd);
    this.track?.removeEventListener('transitionend', this.onTransitionEnd);
    window.removeEventListener('resize', this.onResize);
  }

  isMobilePaired() {
    return this.mobileMq.matches;
  }

  getVisibleCount() {
    return this.isMobilePaired() ? 1 : 2;
  }

  getSlideSelector() {
    return this.isMobilePaired()
      ? '.exposure-collection-panels__page:not(.exposure-collection-panels__page--clone)'
      : '.exposure-collection-panels__item:not(.exposure-collection-panels__item--clone)';
  }

  getCloneClass() {
    return this.isMobilePaired()
      ? 'exposure-collection-panels__page--clone'
      : 'exposure-collection-panels__item--clone';
  }

  enableMobilePairedCarousel() {
    this.teardownClones();
    this.section?.classList.add('is-mobile-paired');
    this.section?.classList.remove('is-carousel-ready');
    this.prevBtn?.setAttribute('hidden', '');
    this.nextBtn?.setAttribute('hidden', '');
    this.buildMobilePages();
    this.setupInfiniteTrack();
    this.syncMobilePageWidths();
    this.preloadImages();
    this.positionTrack(false);
    requestAnimationFrame(() => {
      this.syncMobilePageWidths();
      this.positionTrack(false);
      requestAnimationFrame(() => {
        this.syncMobilePageWidths();
        this.positionTrack(false);
        this.section?.classList.add('is-carousel-ready');
        this.startAutoplay();
      });
    });
  }

  enableDesktopCarousel() {
    this.teardownClones();
    this.section?.classList.remove('is-mobile-paired');
    this.clearMobilePageWidths();
    this.unwrapMobilePages();
    this.prevBtn?.removeAttribute('hidden');
    this.nextBtn?.removeAttribute('hidden');
    this.section?.classList.remove('is-carousel-ready');
    this.setupInfiniteTrack();
    this.preloadImages();
    this.positionTrack(false);
    requestAnimationFrame(() => {
      this.positionTrack(false);
      requestAnimationFrame(() => {
        this.positionTrack(false);
        this.section?.classList.add('is-carousel-ready');
        this.startAutoplay();
      });
    });
  }

  buildMobilePages() {
    if (this.mobilePagesReady) return;

    const items = Array.from(
      this.track.querySelectorAll('.exposure-collection-panels__item:not(.exposure-collection-panels__item--clone)')
    );

    for (let i = 0; i < items.length; i += CollectionPanelsCarousel.PANELS_PER_MOBILE_PAGE) {
      const page = document.createElement('div');
      page.className = 'exposure-collection-panels__page';
      page.dataset.carouselPage = '';
      page.appendChild(items[i]);
      if (items[i + 1]) page.appendChild(items[i + 1]);
      this.track.appendChild(page);
    }

    this.mobilePagesReady = true;
  }

  unwrapMobilePages() {
    if (!this.mobilePagesReady) return;

    this.teardownClones();

    const pages = Array.from(this.track.querySelectorAll('.exposure-collection-panels__page'));
    const items = [];

    pages.forEach((page) => {
      page.querySelectorAll('.exposure-collection-panels__item').forEach((item) => {
        items.push(item);
      });
      page.remove();
    });

    items.forEach((item) => this.track.appendChild(item));
    this.mobilePagesReady = false;
    this.originalItems = items;
  }

  teardownClones() {
    if (!this.clonesReady) return;

    const cloneClass = this.getCloneClass();
    this.track.querySelectorAll(`.${cloneClass}`).forEach((clone) => {
      clone.remove();
    });

    this.clonesReady = false;
    this.slides = Array.from(this.track.querySelectorAll(this.getSlideSelector()));
    this.originalItems = this.slides;
    this.positionCount = this.slides.length;
    this.index = Math.min(this.index, Math.max(this.positionCount, 1));
    if (this.index < 1) this.index = 1;
  }

  setupInfiniteTrack() {
    if (this.clonesReady) return;

    const originals = Array.from(this.track.querySelectorAll(this.getSlideSelector()));

    if (originals.length === 0) return;

    this.originalItems = originals;
    this.positionCount = originals.length;

    const visible = this.getVisibleCount();
    const leadingClone = originals[originals.length - 1];
    const trailingClones = originals.slice(0, visible);
    const cloneClass = this.getCloneClass();

    this.track.insertBefore(this.cloneSlide(leadingClone, cloneClass), this.track.firstChild);
    trailingClones.forEach((slide) => {
      this.track.appendChild(this.cloneSlide(slide, cloneClass));
    });

    this.slides = Array.from(
      this.isMobilePaired()
        ? this.track.querySelectorAll('.exposure-collection-panels__page')
        : this.track.querySelectorAll('.exposure-collection-panels__item')
    );
    this.clonesReady = true;
    this.syncSectionMetrics();
  }

  syncSectionMetrics() {
    const slideCount = this.slides?.length ?? this.originalItems?.length ?? 0;
    const visible = this.getVisibleCount();
    this.section?.style.setProperty('--carousel-slide-count', String(slideCount));
    this.section?.style.setProperty('--carousel-visible', String(visible));
    this.track?.style.setProperty('--carousel-index', String(this.index));
  }

  updateVisiblePanelPositions() {
    this.track
      ?.querySelectorAll('.exposure-collection-panels__item')
      .forEach((item) => {
        item.classList.remove(
          'exposure-collection-panels__item--visible-left',
          'exposure-collection-panels__item--visible-right'
        );
      });

    if (!this.slides?.length || this.isMobilePaired()) return;

    const visibleSlides = this.slides.slice(this.index, this.index + this.getVisibleCount());
    visibleSlides.forEach((slide, index) => {
      if (!slide?.classList?.contains('exposure-collection-panels__item')) return;

      if (index === 0) {
        slide.classList.add('exposure-collection-panels__item--visible-left');
      } else if (index === 1) {
        slide.classList.add('exposure-collection-panels__item--visible-right');
      }
    });
  }

  cloneSlide(slide, cloneClass) {
    const clone = slide.cloneNode(true);
    clone.classList.add(cloneClass);
    clone.setAttribute('aria-hidden', 'true');
    clone.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach((heading) => {
      const replacement = document.createElement('p');
      Array.from(heading.attributes).forEach((attribute) => {
        replacement.setAttribute(attribute.name, attribute.value);
      });
      replacement.innerHTML = heading.innerHTML;
      heading.replaceWith(replacement);
    });

    if (clone.matches('a[href]')) {
      clone.setAttribute('tabindex', '-1');
    }

    clone.querySelectorAll('a[href]').forEach((link) => {
      link.setAttribute('tabindex', '-1');
    });

    return clone;
  }

  preloadImages() {
    const root = this.track;
    root.querySelectorAll('img').forEach((img) => {
      if (img.loading === 'lazy') img.loading = 'eager';
      if (img.complete) return;
      if (typeof img.decode === 'function') img.decode().catch(() => {});
    });
  }

  syncMobilePageWidths() {
    if (!this.isMobilePaired() || !this.viewport) return;

    const pageWidth = this.viewport.clientWidth;
    if (pageWidth <= 0) return;

    const width = `${pageWidth}px`;
    this.track.querySelectorAll('.exposure-collection-panels__page').forEach((page) => {
      page.style.flex = `0 0 ${width}`;
      page.style.width = width;
      page.style.minWidth = width;
      page.style.maxWidth = width;
    });
  }

  clearMobilePageWidths() {
    this.track?.querySelectorAll('.exposure-collection-panels__page').forEach((page) => {
      page.style.flex = '';
      page.style.width = '';
      page.style.minWidth = '';
      page.style.maxWidth = '';
    });
  }

  getOffsetPx() {
    const slide = this.slides?.[this.index];
    if (!slide) return 0;
    return slide.offsetLeft;
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  onResize() {
    const wasMobile = this.section?.classList.contains('is-mobile-paired');
    const isMobile = this.isMobilePaired();

    if (wasMobile !== isMobile) {
      if (isMobile) {
        this.enableMobilePairedCarousel();
      } else {
        this.enableDesktopCarousel();
      }
      return;
    }

    if (this.isMobilePaired()) {
      this.syncMobilePageWidths();
    }

    this.syncSectionMetrics();
    this.positionTrack(false);
  }

  onPrev() {
    if (this.isAnimating) return;
    this.resetAutoplay();
    this.index -= 1;
    if (this.prefersReducedMotion()) {
      this.snapCloneBoundary();
      this.positionTrack(false);
      return;
    }
    this.positionTrack(true);
  }

  onNext() {
    if (this.isAnimating) return;
    this.resetAutoplay();
    this.index += 1;
    if (this.prefersReducedMotion()) {
      this.snapCloneBoundary();
      this.positionTrack(false);
      return;
    }
    this.positionTrack(true);
  }

  snapCloneBoundary() {
    if (this.index <= 0) {
      this.index = this.positionCount;
    } else if (this.index > this.positionCount) {
      this.index = 1;
    }
  }

  advanceAutoplay() {
    this.onNext();
  }

  onTransitionEnd(event) {
    if (event.target !== this.track || event.propertyName !== 'transform') return;

    this.isAnimating = false;

    if (this.index <= 0) {
      this.setIndexInstant(this.positionCount);
    } else if (this.index > this.positionCount) {
      this.setIndexInstant(1);
    }
  }

  setIndexInstant(index) {
    this.index = index;
    this.positionTrack(false);
  }

  startAutoplay() {
    this.stopAutoplay();
    if (this.prefersReducedMotion() || this.positionCount <= 1) return;

    this.autoplayTimer = window.setInterval(() => {
      this.advanceAutoplay();
    }, CollectionPanelsCarousel.AUTOPLAY_MS);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  resetAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }

  onTouchStart(event) {
    const touch = event.touches[0] ?? event.changedTouches[0];
    this.touchStartX = touch?.clientX ?? 0;
    this.touchStartY = touch?.clientY ?? 0;
  }

  onTouchMove(event) {
    if (!this.isMobilePaired()) return;
    const touch = event.touches[0];
    if (!touch) return;

    const deltaX = Math.abs(touch.clientX - this.touchStartX);
    const deltaY = Math.abs(touch.clientY - this.touchStartY);

    if (deltaX > deltaY) {
      event.preventDefault();
    }
  }

  onTouchEnd(event) {
    const touch = event.changedTouches[0];
    if (!touch) return;

    const deltaX = touch.clientX - this.touchStartX;
    const deltaY = touch.clientY - this.touchStartY;

    if (Math.abs(deltaX) < 48 || Math.abs(deltaX) < Math.abs(deltaY)) return;

    if (deltaX < 0) this.onNext();
    else this.onPrev();
  }

  positionTrack(animate = false) {
    if (!this.viewport || !this.track) return;

    if (this.isMobilePaired()) {
      this.syncMobilePageWidths();
    }

    this.syncSectionMetrics();

    if (animate && !this.prefersReducedMotion()) {
      this.isAnimating = true;
      this.track.style.transition = '';
    } else {
      this.isAnimating = false;
      this.track.style.transition = 'none';
    }

    const offset = this.getOffsetPx();
    this.track.style.transform = `translate3d(${-offset}px, 0, 0)`;
    this.updateVisiblePanelPositions();

    if (!animate) {
      this.track.offsetHeight;
      this.track.style.transition = '';
    }

    this.prevBtn?.removeAttribute('disabled');
    this.nextBtn?.removeAttribute('disabled');
  }
}

if (!customElements.get('collection-panels-carousel')) {
  customElements.define('collection-panels-carousel', CollectionPanelsCarousel);
}
